/**
 * Toast notification - Apple-style clean design
 * Supports optional action button for undo functionality
 */

import { useEffect, useState, useMemo, memo, type ReactNode } from 'react';
import { Sparkles, Heart, X, Star, Check } from '@/lib/icons';
import { triggerHapticSuccess, triggerHaptic } from '@/lib/haptics';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  message: string;
  subMessage?: string;
  icon?: ReactNode;
  duration?: number;
  onClose: () => void;
  variant?: 'success' | 'info' | 'error';
  action?: ToastAction;
}

export const Toast = memo(function Toast({
  message,
  subMessage,
  icon,
  duration = 3500,
  onClose,
  variant = 'success',
  action,
}: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleAction = () => {
    triggerHaptic();
    action?.onClick();
    onClose();
  };

  // Calm Botanical toast variants
  const variantStyles = {
    success: 'bg-primary-700 text-white',
    info: 'bg-ink text-white',
    error: 'bg-red-600 text-white',
  };

  // Default icons by variant (white for contrast on blue/dark backgrounds)
  const defaultIcons = {
    success: <Check className="w-4 h-4 text-white" />,
    info: <Star className="w-4 h-4 text-white" />,
    error: <X className="w-4 h-4 text-white" />,
  };

  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 flex justify-center motion-safe:animate-slide-up">
      <div
        className={`${variantStyles[variant]} px-4 py-3.5 rounded-card shadow-lg max-w-sm w-full flex items-center gap-3 backdrop-blur-xl`}
        role="alert"
      >
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-white">
          {displayIcon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-body tracking-[-0.01em] text-white">{message}</p>
          {subMessage && <p className="text-label-sm text-white/90 mt-0.5">{subMessage}</p>}
        </div>
        {action && (
          <button
            onClick={handleAction}
            className="flex-shrink-0 px-3 py-1.5 rounded-chip bg-white/20 hover:bg-white/30
                     motion-safe:transition-colors duration-150 text-label-sm font-semibold text-white"
          >
            {action.label}
          </button>
        )}
        <button
          onClick={onClose}
          className="flex-shrink-0 p-2 rounded-full hover:bg-white/20 motion-safe:transition-colors duration-150 text-white"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

/**
 * Confetti particle for celebration
 */
function ConfettiParticle({ delay, color, left }: { delay: number; color: string; left: number }) {
  return (
    <div
      className="absolute w-2 h-2 rounded-full motion-safe:animate-confetti"
      style={{
        backgroundColor: color,
        left: `${left}%`,
        animationDelay: `${delay}ms`,
        top: '-10px',
      }}
    />
  );
}

/**
 * Confetti burst animation
 */
function ConfettiBurst() {
  const particles = useMemo(() => {
    const colors = ['#007AFF', '#38A3F5', '#818cf8', '#f472b6', '#fbbf24', '#60A5FA'];
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: Math.random() * 300,
      color: colors[Math.floor(Math.random() * colors.length)],
      left: 10 + Math.random() * 80,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <ConfettiParticle key={p.id} delay={p.delay} color={p.color} left={p.left} />
      ))}
    </div>
  );
}

/**
 * Reward toast with extra celebration and confetti
 */
interface RewardToastProps {
  credits: number;
  message: string;
  onClose: () => void;
}

export const RewardToast = memo(function RewardToast({ credits, message, onClose }: RewardToastProps) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Trigger celebration haptic on mount
    triggerHapticSuccess();

    const timer = setTimeout(onClose, 5000);
    const confettiTimer = setTimeout(() => setShowConfetti(false), 2000);
    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, [onClose]);

  return (
    <>
      {/* Confetti overlay */}
      {showConfetti && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          <ConfettiBurst />
        </div>
      )}

      {/* Toast */}
      <div className="fixed bottom-24 left-4 right-4 z-50 flex justify-center motion-safe:animate-celebrate">
        <div
          className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 text-white px-6 py-5 rounded-sheet shadow-glow max-w-sm w-full overflow-hidden"
          role="alert"
        >
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent motion-safe:animate-shimmer" />

          <div className="relative flex items-center gap-4">
            {/* Animated icon */}
            <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-card flex items-center justify-center motion-safe:animate-bounce-soft">
              <div className="relative">
                <Sparkles className="w-7 h-7 text-white" />
                <Heart className="absolute -top-1 -right-1 w-4 h-4 text-pink-200 motion-safe:animate-pulse" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-xl text-white">+{credits}</p>
                <span className="text-sm font-medium text-white/90">credits</span>
              </div>
              <p className="text-sm text-white/90 mt-1 leading-relaxed">{message}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-button hover:bg-white/20 motion-safe:transition-colors text-white"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
});
